import { supabase } from './supabase';
import { logger } from './logger';
import { isTransientNetworkError } from './supabase-error-handler';

const DEFAULT_SIGNED_URL_TTL = 3600; // 1 hour

// Supabase Storage rejects a createSignedUrls request carrying more than 1000
// paths with HTTP 400 "body/paths must NOT have more than 1000 items"
// (HOK-OS-47). Split larger inputs into batches of this size — the storage-API
// twin of the PostgREST 1000-row cap.
const CREATE_SIGNED_URLS_MAX_BATCH = 1000;

// HTTP statuses Supabase Storage returns when it's transiently overloaded rather
// than when the request is malformed: 429 ("Too many connections issued to the
// database" / rate limit) and 503/504 gateway blips. These are retryable and
// non-actionable — back off and retry, and don't page Sentry. Scoped to this
// storage layer on purpose: a *sustained* 429 storm in another code path (e.g. an
// accidental fetch loop, cf. the vault infinite-loop) should still surface there.
const TRANSIENT_STORAGE_STATUSES = new Set([429, 503, 504]);

// Backoff before each retry of an idempotent signed-URL call. Two entries = two
// retries after the initial attempt; short waits so a momentary Storage DB-pool
// blip self-heals before the user perceives a stall (HOK-OS-49).
const SIGNED_URL_RETRY_BACKOFFS_MS = [150, 400];

/**
 * True when a Supabase StorageApiError carries a transient "server busy" HTTP
 * status (429/503/504) — retryable overload, not a client bug.
 */
function isTransientStorageStatus(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  const status = Number(e.status ?? e.statusCode);
  return Number.isFinite(status) && TRANSIENT_STORAGE_STATUSES.has(status);
}

/** Retryable = a transient network blip OR a transient "server busy" HTTP status. */
function isRetryableStorageError(error: unknown): boolean {
  return isTransientNetworkError(error) || isTransientStorageStatus(error);
}

/**
 * True when Storage reports the object as not found (404 "Object not found").
 *
 * On a PRIVATE bucket this status is returned both when the object is genuinely
 * gone AND when RLS denies the request — Storage masks the authz denial as
 * not-found so it can't be used to probe object existence. The common cause of
 * the latter is a stale/expired token (Mobile Safari background-tab throttling
 * breaks auto-refresh), which drops the caller to `anon`. Either way the caller
 * renders without the image, so this is an expected, non-actionable miss — warn
 * rather than page Sentry per occurrence (HOK-OS-47). A dangling DB path is
 * better caught by a periodic audit than a per-request error.
 */
function isObjectNotFoundError(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const e = error as Record<string, unknown>;
  if (Number(e.statusCode) === 404 || Number(e.status) === 404) return true;
  return typeof e.message === 'string' && /object not found/i.test(e.message);
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Run an idempotent Supabase signed-URL call with bounded retry-with-backoff on
 * transient failures (network blip or 429/503/504). createSignedUrl(s) is
 * read-only, so re-issuing is safe and lets a momentary Storage DB-pool
 * exhaustion recover before we give up and render without a URL (HOK-OS-49).
 */
async function withSignedUrlRetry<T>(
  run: () => Promise<{ data: T | null; error: unknown }>,
): Promise<{ data: T | null; error: unknown }> {
  for (let attempt = 0; ; attempt++) {
    let result: { data: T | null; error: unknown };
    try {
      result = await run();
    } catch (err) {
      result = { data: null, error: err };
    }
    if (
      !result.error ||
      attempt >= SIGNED_URL_RETRY_BACKOFFS_MS.length ||
      !isRetryableStorageError(result.error)
    ) {
      return result;
    }
    await sleep(SIGNED_URL_RETRY_BACKOFFS_MS[attempt]);
  }
}

/**
 * Extract the diagnostic fields off a Supabase StorageError into a plain object.
 *
 * Sentry's exception capture keeps `name`/`message` but drops custom own-properties
 * like `status`/`statusCode` (those need the ExtraErrorData integration, which we
 * don't enable). Serializing them explicitly into the log context guarantees the
 * HTTP status survives — that's what tells a real API error apart from a transient
 * network blip when triaging.
 */
function serializeStorageError(error: unknown): Record<string, unknown> | undefined {
  if (!error || typeof error !== 'object') {
    return error == null ? undefined : { value: String(error) };
  }
  const e = error as Record<string, unknown>;
  return {
    name: e.name,
    message: e.message,
    status: e.status,
    statusCode: e.statusCode,
  };
}

export interface SignedUrlInfo {
  path: string;
  signedUrl: string | null;
  expiresAt: number | null;
}

/**
 * Generate a signed URL for a single storage object path.
 *
 * Pass `options.download` to force a download response with the given filename
 * (string) or the object's own name (true). Forwarded straight to the Supabase
 * createSignedUrl call, so existing callers that omit it are unaffected.
 */
export async function createSignedStorageUrl(
  bucket: string,
  path: string | null | undefined,
  expiresIn: number = DEFAULT_SIGNED_URL_TTL,
  options?: { download?: string | boolean }
): Promise<SignedUrlInfo> {
  if (!path) {
    return { path: '', signedUrl: null, expiresAt: null };
  }

  const cleanedPath = path.trim().replace(/^\/+/, '');

  const { data, error } = await withSignedUrlRetry<{ signedUrl: string }>(() =>
    supabase.storage.from(bucket).createSignedUrl(cleanedPath, expiresIn, options),
  );

  if (error || !data?.signedUrl) {
    if (isTransientNetworkError(error) || isTransientStorageStatus(error)) {
      console.warn('[Storage] Transient error generating signed URL (non-critical)', { bucket, path: cleanedPath });
    } else if (isObjectNotFoundError(error)) {
      // Object missing or RLS-denied (e.g. stale Safari token) — the caller
      // degrades gracefully, so this is non-actionable noise (HOK-OS-47).
      console.warn('[Storage] Signed URL skipped — object not found or not authorized (non-critical)', { bucket, path: cleanedPath });
    } else if (error) {
      logger.error('[Storage] Failed to generate signed URL', error, {
        bucket,
        path: cleanedPath,
        storageError: serializeStorageError(error),
      });
    }

    return { path: cleanedPath, signedUrl: null, expiresAt: null };
  }

  return {
    path: cleanedPath,
    signedUrl: data.signedUrl,
    expiresAt: Date.now() + expiresIn * 1000,
  };
}

/**
 * Generate signed URLs for a single batch of already-cleaned paths.
 *
 * The caller guarantees `cleanedPaths` is non-empty and within Supabase's
 * per-request cap (`CREATE_SIGNED_URLS_MAX_BATCH`). Transient failures retry
 * with backoff; a hard failure returns null URLs for every path in the batch.
 */
async function createSignedUrlsForBatch(
  bucket: string,
  cleanedPaths: string[],
  expiresIn: number,
): Promise<Record<string, SignedUrlInfo>> {
  const { data, error } = await withSignedUrlRetry<{ path: string; signedUrl: string }[]>(
    // createSignedUrls echoes back each input path (non-null in practice); cast
    // away the SDK's `path: string | null` per-item type to keep the existing
    // reduce keyed on a plain string, as the original typed local did.
    () =>
      supabase.storage.from(bucket).createSignedUrls(cleanedPaths, expiresIn) as Promise<{
        data: { path: string; signedUrl: string }[] | null;
        error: unknown;
      }>,
  );

  if (error || !data) {
    if (isTransientNetworkError(error) || isTransientStorageStatus(error)) {
      console.warn('[Storage] Transient error generating bulk signed URLs (non-critical)', { bucket, pathCount: cleanedPaths.length });
    } else if (isObjectNotFoundError(error)) {
      // Objects missing or RLS-denied (e.g. stale Safari token) — callers
      // degrade gracefully, so this is non-actionable noise (HOK-OS-47).
      console.warn('[Storage] Bulk signed URLs skipped — object not found or not authorized (non-critical)', { bucket, pathCount: cleanedPaths.length });
    } else {
      logger.error('[Storage] Failed to generate bulk signed URLs', error, {
        bucket,
        pathCount: cleanedPaths.length,
        samplePaths: cleanedPaths.slice(0, 3),
        storageError: serializeStorageError(error),
      });
    }

    return cleanedPaths.reduce<Record<string, SignedUrlInfo>>((acc, path) => {
      acc[path] = { path, signedUrl: null, expiresAt: null };
      return acc;
    }, {});
  }

  return data.reduce<Record<string, SignedUrlInfo>>((acc, item) => {
    const signedUrl = item.signedUrl || null;
    acc[item.path] = {
      path: item.path,
      signedUrl,
      expiresAt: signedUrl ? Date.now() + expiresIn * 1000 : null,
    };
    return acc;
  }, {});
}

/**
 * Generate signed URLs for multiple storage object paths at once.
 *
 * Inputs larger than Supabase's per-request cap are split into batches
 * (HOK-OS-47) so a member with 1000+ photos can't blow the whole call up;
 * each batch retries and fails independently, and the results are merged.
 */
export async function createSignedStorageUrls(
  bucket: string,
  paths: string[],
  expiresIn: number = DEFAULT_SIGNED_URL_TTL
): Promise<Record<string, SignedUrlInfo>> {
  if (!paths.length) {
    return {};
  }

  const cleanedPaths = paths
    .filter(Boolean)
    .map((path) => path.trim().replace(/^\/+/, ''));

  if (!cleanedPaths.length) {
    return {};
  }

  // Single batch is the overwhelmingly common case — skip the merge overhead.
  if (cleanedPaths.length <= CREATE_SIGNED_URLS_MAX_BATCH) {
    return createSignedUrlsForBatch(bucket, cleanedPaths, expiresIn);
  }

  // Batches run sequentially: a 1000+ path fan-out is exactly the load the
  // transient-429 backoff exists for, so we don't want to hammer Storage with
  // every batch in parallel and manufacture the overload we retry around.
  const merged: Record<string, SignedUrlInfo> = {};
  for (let i = 0; i < cleanedPaths.length; i += CREATE_SIGNED_URLS_MAX_BATCH) {
    const batch = cleanedPaths.slice(i, i + CREATE_SIGNED_URLS_MAX_BATCH);
    Object.assign(merged, await createSignedUrlsForBatch(bucket, batch, expiresIn));
  }
  return merged;
}

/**
 * Calculate milliseconds until a signed URL should be refreshed.
 * Returns null when no refresh is needed (missing expiry or already expired beyond buffer).
 */
export function getSignedUrlRefreshDelay(
  expiresAt: number | null | undefined,
  bufferSeconds = 60
): number | null {
  if (!expiresAt) {
    return null;
  }

  const now = Date.now();
  const refreshAt = expiresAt - bufferSeconds * 1000;
  const delay = refreshAt - now;

  if (delay <= 0) {
    return 0; // refresh immediately
  }

  return delay;
}
